<template>
    <div>
        <div v-if="!isLoading" class="bg-gray-100 overflow-hidden h-full">
            <div
                class="m-8 space-y-6 sm:px-6 lg:px-0 lg:col-span-9"
                :class="{
                    'opacity-50': isEditAdmin || isCreateAdmin
                }"
            >
                <section aria-labelledby="payment_details_heading">
                    <div
                        class="shadow relative bg-white rounded-md sm:overflow-hidden flex"
                        :class="{ 'pb-8': this.user.role === 1 }"
                    >
                        <div class="py-6 px-4 pl-6 pb-10 flex-1">
                            <div class="flex items-center">
                                <h2
                                    class="text-xl leading-6 font-bold text-gray-900 text-left"
                                >
                                    {{ $t("setting.profile") }}
                                </h2>
                                <span
                                    v-if="user.role === 1"
                                    class="bg-orange-500 px-2 py-1 ml-3 rounded-3xl text-md text-white"
                                    >{{ $t("setting.staff") }}</span
                                >
                                <span
                                    v-if="user.role === 2"
                                    class="bg-orange-400 px-2 py-1 ml-3 rounded-3xl text-md text-white"
                                    >{{ $t("setting.super_admin") }}</span
                                >
                                <span
                                    v-if="user.role === 3"
                                    class="bg-orange-300 px-2 py-1 ml-3 rounded-3xl text-md text-white"
                                    >{{ $t("setting.admin") }}</span
                                >
                            </div>

                            <div
                                v-if="!isLoading && userData.profile"
                                class="mt-6 grid grid-cols-4 gap-6"
                            >
                                <div class="col-span-2">
                                    <label
                                        for="first_name"
                                        class="block text-sm font-medium text-gray-700 text-left"
                                        >{{ $t("setting.f_name") }}</label
                                    >
                                    <input
                                        type="text"
                                        name="first_name"
                                        id="first_name"
                                        ref="first_name"
                                        @focus="isEdit = true"
                                        autocomplete="cc-given-name"
                                        :value="userData.profile.given_name"
                                        class="mt-1 block border w-full border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm"
                                    />
                                </div>

                                <div class="col-span-2">
                                    <label
                                        for="last_name"
                                        class="block text-sm font-medium text-gray-700 text-left"
                                        >{{ $t("setting.l_name") }}</label
                                    >
                                    <input
                                        type="text"
                                        name="last_name"
                                        id="last_name"
                                        ref="last_name"
                                        @focus="isEdit = true"
                                        autocomplete="cc-family-name"
                                        :value="userData.profile.surname"
                                        class="mt-1 block border border-gray-300 w-full rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm"
                                    />
                                </div>

                                <div
                                    v-if="this.user.role !== 1"
                                    class="col-span-4 sm:col-span-2"
                                >
                                    <label
                                        for="job_title"
                                        class="block text-sm font-medium text-gray-700 text-left"
                                        >{{ $t("setting.job_title") }}</label
                                    >
                                    <input
                                        type="text"
                                        name="job_title"
                                        id="job_title"
                                        ref="job_title"
                                        @focus="isEdit = true"
                                        :value="userData.profile.job_title"
                                        class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm"
                                    />
                                </div>

                                <div class="col-span-4 sm:col-span-2">
                                    <label
                                        for="email_address"
                                        class="block text-sm font-medium text-gray-700 text-left"
                                        >{{
                                            $t("setting.contact_email")
                                        }}</label
                                    >
                                    <input
                                        type="text"
                                        name="email_address"
                                        id="email_address"
                                        ref="email"
                                        @focus="isEdit = true"
                                        autocomplete="email"
                                        :value="userData.profile.email"
                                        class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm"
                                    />
                                </div>

                                <div class="col-span-4 sm:col-span-2">
                                    <label
                                        for="locale"
                                        class="block text-sm text-left font-medium text-gray-700"
                                        >{{ $t("setting.locale") }}</label
                                    >
                                    <select
                                        id="locale"
                                        name="locale"
                                        ref="locale"
                                        @focus="isEdit = true"
                                        class="mt-1 block w-full bg-white border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm"
                                    >
                                        <option
                                            v-for="(locale, index) in locales"
                                            :value="locale.code"
                                            :key="index"
                                            :selected="
                                                locale.code === $i18n.locale
                                            "
                                        >
                                            {{ locale.name }}
                                        </option>
                                    </select>
                                </div>
                                <div></div>
                            </div>
                        </div>
                        <div class="absolute bottom-0 w-full">
                            <div class="flex mr-4 justify-end items-center">
                                <button
                                    @click="changePassword"
                                    :disabled="changePasswordButton"
                                    class="bg-black rounded-md text-white py-2 px-6 mb-6 mr-3"
                                    :class="{
                                        'opacity-30': changePasswordButton
                                    }"
                                >
                                    {{ $t("setting.change_pw") }}
                                </button>
                                <button
                                    @click="discardChanges"
                                    class="bg-white border border-gray-400 rounded-md text-black py-2 px-6 mb-6 mr-3"
                                >
                                    {{ $t("button.discard") }}
                                </button>
                                <button
                                    @click="saveSetting"
                                    :disabled="saveSettingButton"
                                    class="bg-black rounded-md text-white py-2 px-6 mb-6 transition duration-500 ease-in-out"
                                    :class="{
                                        'opacity-30': saveSettingButton
                                    }"
                                >
                                    {{ $t("button.save") }}
                                </button>
                            </div>
                        </div>
                        <!--                        <div-->
                        <!--                            class="flex flex-col min-w-1/3 justify-between text-right px-4"-->
                        <!--                        >-->
                        <!--                            <div></div>-->
                        <!--                            <div class="absolute bottom-0 right-0 mr-10">-->
                        <!--                                <button-->
                        <!--                                    @click="changePassword"-->
                        <!--                                    :disabled="changePasswordButton"-->
                        <!--                                    class="bg-black rounded-md text-white py-2 px-8 mb-6 mr-3 transition duration-500 ease-in-out"-->
                        <!--                                    :class="{-->
                        <!--                                        'opacity-30': changePasswordButton-->
                        <!--                                    }"-->
                        <!--                                >-->
                        <!--                                    {{ $t("setting.change_pw") }}-->
                        <!--                                </button>-->
                        <!--                                <button-->
                        <!--                                    @click="discardChanges"-->
                        <!--                                    class="bg-white border border-gray-400 rounded-md text-black py-2 px-8 mb-6 mr-3"-->
                        <!--                                >-->
                        <!--                                    {{ $t("button.discard") }}-->
                        <!--                                </button>-->
                        <!--                                <button-->
                        <!--                                    @click="saveSetting"-->
                        <!--                                    :disabled="saveSettingButton"-->
                        <!--                                    class="bg-black rounded-md text-white py-2 px-8 mb-6 transition duration-500 ease-in-out"-->
                        <!--                                    :class="{-->
                        <!--                                        'opacity-30': saveSettingButton-->
                        <!--                                    }"-->
                        <!--                                >-->
                        <!--                                    {{ $t("button.save") }}-->
                        <!--                                </button>-->
                        <!--                            </div>-->
                        <!--                        </div>-->
                    </div>
                </section>
                <section aria-labelledby="payment_details_heading">
                    <div
                        v-if="!isLoading && userData.enterprise"
                        class="shadow bg-white sm:rounded-md sm:overflow-hidden flex"
                    >
                        <div class="py-6 px-4 pl-6 flex-1">
                            <h2
                                id="payment_details_heading"
                                class="text-xl leading-6 font-bold text-gray-900 text-left"
                            >
                                {{ $t("setting.company") }}
                            </h2>

                            <div class="mt-6 grid grid-cols-4 gap-6">
                                <div class="col-span-4 md:col-span-2">
                                    <label
                                        for="company_name"
                                        class="block text-sm font-medium text-gray-700 text-left"
                                        >{{ $t("setting.company_name") }}</label
                                    >
                                    <input
                                        type="text"
                                        name="company_name"
                                        id="company_name"
                                        readonly
                                        :value="this.userData.enterprise.name"
                                        class="mt-1 block border w-full border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm"
                                    />
                                </div>

                                <div class="col-span-2">
                                    <label
                                        v-if="this.user.role !== 1"
                                        for="business_number"
                                        class="block text-sm font-medium text-gray-700 text-left"
                                        >{{ $t("setting.business_num") }}</label
                                    >
                                    <input
                                        v-if="this.user.role !== 1"
                                        type="text"
                                        name="business_number"
                                        id="business_number"
                                        readonly
                                        :value="
                                            this.userData.enterprise
                                                .business_phone
                                        "
                                        class="mt-1 block border border-gray-300 w-full rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm"
                                    />
                                </div>
                            </div>
                            <div
                                v-if="this.userData.enterprise.address"
                                class="mt-6 grid grid-cols-6 gap-6"
                            >
                                <div class="col-span-6 lg:col-span-2">
                                    <label
                                        for="address_1"
                                        class="block text-sm font-medium text-gray-700 text-left"
                                        >{{ $t("setting.add_1") }}</label
                                    >
                                    <input
                                        type="text"
                                        name="address_1"
                                        id="address_1"
                                        readonly
                                        :value="
                                            this.userData.enterprise.address[0]
                                        "
                                        class="mt-1 block border w-full border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm"
                                    />
                                </div>

                                <div class="col-span-6 lg:col-span-2">
                                    <label
                                        for="address_2"
                                        class="block text-sm font-medium text-gray-700 text-left"
                                        >{{ $t("setting.add_2") }}</label
                                    >
                                    <input
                                        type="text"
                                        name="address_2"
                                        id="address_2"
                                        readonly
                                        :value="
                                            this.userData.enterprise.address[1]
                                        "
                                        class="mt-1 block border w-full border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm"
                                    />
                                </div>
                                <div class="col-span-6 lg:col-span-2">
                                    <label
                                        for="address_3"
                                        class="block text-sm font-medium text-gray-700 text-left"
                                        >{{ $t("setting.add_3") }}</label
                                    >
                                    <input
                                        type="text"
                                        name="address_3"
                                        id="address_3"
                                        readonly
                                        :value="
                                            this.userData.enterprise.address[2]
                                        "
                                        class="mt-1 block border w-full border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm"
                                    />
                                </div>
                            </div>
                        </div>

                        <div
                            class="flex flex-col min-w-1/3 justify-between text-right px-4"
                        ></div>
                    </div>
                </section>
                <section v-if="user.role === 2">
                    <div
                        class="shadow bg-white sm:rounded-md sm:overflow-hidden flex"
                    >
                        <div class="py-6 w-full">
                            <div class="flex justify-between">
                                <h2
                                    class="ml-4 text-xl leading-6 font-bold text-gray-900 text-left"
                                >
                                    {{ $t("setting.admin_list") }}
                                </h2>

                                <button
                                    class="mr-6 text-cyan-700 hover:text-cyan-900"
                                    @click="createAdmin"
                                >
                                    {{ $t("setting.invite_admin") }}
                                </button>
                            </div>
                            <div
                                class="align-middle min-w-full overflow-x-scroll"
                            >
                                <table
                                    class="table-auto min-w-full border rounded-2xl mt-4 divide-y divide-gray-200"
                                >
                                    <thead>
                                        <tr>
                                            <th
                                                class="whitespace-nowrap pl-4 py-5 bg-gray-50 text-left text-sm border-r font-medium text-gray-500 tracking-wider"
                                            >
                                                {{ $t("setting.admin_name") }}
                                            </th>
                                            <th
                                                class="whitespace-nowrap pl-4 py-3 bg-gray-50 text-left text-sm border-r font-medium text-gray-500 tracking-wider"
                                            >
                                                {{ $t("setting.admin_type") }}
                                            </th>
                                            <th
                                                class="whitespace-nowrap pl-4 py-3 bg-gray-50 text-left text-sm border-r font-medium text-gray-500 tracking-wider"
                                            >
                                                {{ $t("setting.added_date") }}
                                            </th>
                                            <th
                                                class="whitespace-nowrap pl-4 py-3 bg-gray-50 text-left text-sm font-medium text-gray-500 tracking-wider"
                                            >
                                                {{ $t("setting.actions") }}
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody
                                        class="bg-white divide-y divide-gray-200"
                                    >
                                        <tr
                                            class="bg-white text-left font-medium text-tiny"
                                            v-for="(user,
                                            index) in adminList.list"
                                            :key="index"
                                        >
                                            <td class="border-r  pl-4 py-3">
                                                {{ user.given_name }}
                                                {{ user.surname }}
                                                <br />
                                                {{ user.email }}
                                            </td>
                                            <td
                                                class="border-r whitespace-nowrap pl-4"
                                            >
                                                <span
                                                    v-if="user.admin_role === 2"
                                                    >Super admin</span
                                                >
                                                <span
                                                    v-if="user.admin_role === 3"
                                                    >Admin</span
                                                >
                                            </td>
                                            <td class="border-r pl-4">
                                                {{ user.add_time }}
                                            </td>
                                            <td class="whitespace-nowrap pl-4">
                                                <div v-if="user.state === 0">
                                                    <button
                                                        @click="
                                                            removeAdmin(
                                                                user.user_id
                                                            )
                                                        "
                                                        class="text-cyan-700 hover:text-cyan-900"
                                                    >
                                                        {{
                                                            $t("button.remove")
                                                        }}</button
                                                    ><span class="mx-1">/</span
                                                    ><button
                                                        @click="
                                                            resendInvite(
                                                                user.user_id
                                                            )
                                                        "
                                                        class="text-cyan-700 hover:text-cyan-900"
                                                    >
                                                        {{
                                                            $t(
                                                                "button.resend_invite"
                                                            )
                                                        }}
                                                    </button>
                                                </div>
                                                <div v-if="user.state === 1">
                                                    <button
                                                        @click="
                                                            removeAdmin(
                                                                user.user_id
                                                            )
                                                        "
                                                        class="text-cyan-700 hover:text-cyan-900"
                                                    >
                                                        {{
                                                            $t("button.remove")
                                                        }}</button
                                                    ><span class="mx-1">/</span>
                                                    <button
                                                        @click="editAdmin(user)"
                                                        class="text-cyan-700 hover:text-cyan-900"
                                                    >
                                                        {{ $t("button.edit") }}
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                            <div
                                v-if="adminList.total_page >= 1"
                                class="mt-8 flex flex-wrap justify-center items-center"
                            >
                                <button
                                    @click="
                                        currentPage > 1
                                            ? (currentPage -= 1)
                                            : ''
                                    "
                                    type="button"
                                    class="px-6 py-1 border rounded-md"
                                >
                                    {{ $t("button.prev_pg") }}
                                </button>
                                <span class="mx-4">
                                    {{ $t("page1") }} {{ currentPage }}/{{
                                        adminList.total_page
                                    }}{{ $t("page2") }}
                                </span>
                                <button
                                    @click="
                                        currentPage < adminList.total_page
                                            ? (currentPage += 1)
                                            : ''
                                    "
                                    type="button"
                                    class="px-6 py-1 border rounded-md"
                                >
                                    {{ $t("button.next_pg") }}
                                </button>
                            </div>
                        </div>
                    </div>
                </section>
                <section v-if="!isLoading && userData.notify_setting">
                    <div
                        class="shadow bg-white sm:rounded-md sm:overflow-hidden pb-8"
                    >
                        <div class="py-6 px-4 pl-6">
                            <h2
                                class="text-xl leading-6 font-bold text-gray-900 text-left"
                            >
                                {{ $t("setting.notification") }}
                            </h2>
                        </div>
                        <table class="table-fixed min-w-full border-b">
                            <thead>
                                <tr>
                                    <th
                                        class="w-full py-1 text-left text-sm text-gray-500"
                                    ></th>
                                </tr>
                            </thead>
                            <tbody class="bg-white divide-y divide-gray-200">
                                <tr>
                                    <td class="text-left py-3 pl-8">
                                        <div>
                                            {{ $t("setting.notify_payment") }}
                                        </div>
                                        <div class="text-gray-400 text-petite">
                                            {{ $t("setting.payment_desc") }}
                                        </div>
                                    </td>
                                    <td class="px-10">
                                        <Switch
                                            :toggle="
                                                !!this.userData.notify_setting
                                                    .payment
                                            "
                                            @onPress="
                                                updateNotify(
                                                    'payment',
                                                    this.userData.notify_setting
                                                        .payment
                                                )
                                            "
                                        />
                                    </td>
                                </tr>
                                <tr>
                                    <td class="text-left py-3 pl-8">
                                        <div>
                                            {{ $t("setting.notify_booking") }}
                                        </div>
                                        <div class="text-gray-400 text-petite">
                                            {{ $t("setting.booking_desc") }}
                                        </div>
                                    </td>
                                    <td class="px-10">
                                        <Switch
                                            :toggle="
                                                !!this.userData.notify_setting
                                                    .reservation
                                            "
                                            @onPress="
                                                updateNotify(
                                                    'reservation',
                                                    this.userData.notify_setting
                                                        .reservation
                                                )
                                            "
                                        />
                                    </td>
                                </tr>
                                <tr>
                                    <td class="text-left py-3 pl-8">
                                        <div>
                                            {{ $t("setting.notify_expiry") }}
                                        </div>
                                        <div class="text-gray-400 text-petite">
                                            {{ $t("setting.expiry_desc") }}
                                        </div>
                                    </td>
                                    <td class="px-10">
                                        <Switch
                                            :toggle="
                                                !!this.userData.notify_setting
                                                    .expire_credit
                                            "
                                            @onPress="
                                                updateNotify(
                                                    'expire_credit',
                                                    this.userData.notify_setting
                                                        .expire_credit
                                                )
                                            "
                                        />
                                    </td>
                                </tr>
                                <tr>
                                    <td class="text-left py-3 pl-8">
                                        <div>
                                            {{ $t("setting.notify_low") }}
                                        </div>
                                        <div class="text-gray-400 text-petite">
                                            {{ $t("setting.low_desc") }}
                                        </div>
                                    </td>
                                    <td class="px-10">
                                        <Switch
                                            :toggle="
                                                !!this.userData.notify_setting
                                                    .low_credit
                                            "
                                            @onPress="
                                                updateNotify(
                                                    'low_credit',
                                                    this.userData.notify_setting
                                                        .low_credit
                                                )
                                            "
                                        />
                                    </td>
                                </tr>
                                <tr>
                                    <td class="text-left py-3 pl-8">
                                        <div>
                                            {{ $t("setting.notify_request") }}
                                        </div>
                                        <div class="text-gray-400 text-petite">
                                            {{ $t("setting.request_desc") }}
                                        </div>
                                    </td>
                                    <td class="px-10">
                                        <Switch
                                            :toggle="
                                                !!this.userData.notify_setting
                                                    .over_limit
                                            "
                                            @onPress="
                                                updateNotify(
                                                    'over_limit',
                                                    this.userData.notify_setting
                                                        .over_limit
                                                )
                                            "
                                        />
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </section>
            </div>
            <AdminSidebar
                ref="admin"
                :isCreateAdmin="isCreateAdmin"
                :isEditAdmin="isEditAdmin"
                :user="selectedAdmin"
                @onDiscard="adminDiscard"
                @onSave="adminSave"
            />
        </div>
        <div v-if="isLoading" class="flex justify-center mt-20 items-center">
            <div class="w-1/5">
                <Spinner />
            </div>
        </div>
    </div>
</template>

<script>
import Switch from "@/components/Switch"
import { Locale } from "@/constants"
import { setSavedLocale } from "@/util/locate"
import AdminSidebar from "@/components/AdminSidebar"
import Spinner from "@/components/Spinner"

export default {
    name: "Setting",
    components: { AdminSidebar, Switch, Spinner },
    props: { user: { required: false } },
    computed: {
        locales() {
            return Locale.SUPPORTED_LOCALES
        }
    },
    emits: ["updateSetting", "disableScroll", "enableScroll"],
    methods: {
        async setLang(lang) {
            this.$i18n.locale = lang
            setSavedLocale(lang)
            await this.$router.push({
                name: this.$route.name,
                params: { lang }
            })
        },
        async getUserData() {
            const token = await this.$auth.getTokenSilently()
            const url =
                process.env.VUE_APP_API_BASE_URL +
                `/setting/profile?user_id=${this.$auth.user.value.sub}&lang=${this.$i18n.locale}`
            this.$axios
                .get(url, {
                    headers: {
                        Authorization: `Bearer ${token}` // send the access token through the 'Authorization' header
                    }
                })
                .then(response => {
                    this.userData = response.data.Data
                    if (this.isLoading === true) {
                        setTimeout(() => {
                            this.isLoading = false
                        }, 500)
                    }
                })
        },
        async getAdminList() {
            const token = await this.$auth.getTokenSilently()
            const url =
                process.env.VUE_APP_API_BASE_URL +
                `/admin/list?user_id=${this.$auth.user.value.sub}&lang=${this.$i18n.locale}`
            this.$axios
                .get(url, {
                    headers: {
                        Authorization: `Bearer ${token}` // send the access token through the 'Authorization' header
                    }
                })
                .then(response => {
                    this.adminList = response.data.Data
                    // if (this.isLoading === true) {
                    //     this.isLoading = false
                    // }
                })
        },
        async removeAdmin(user_id) {
            const token = await this.$auth.getTokenSilently()
            const data = {
                user_id: this.$auth.user.value.sub,
                target_user_id: user_id
            }
            const url =
                process.env.VUE_APP_API_BASE_URL +
                `/admin/invitation/remove?lang=${this.$i18n.locale}`
            this.$axios
                .put(url, data, {
                    headers: {
                        Authorization: `Bearer ${token}` // send the access token through the 'Authorization' header
                    }
                })
                .then(response => {
                    if (response.data.Success) {
                        this.getAdminList()
                    } else alert("Please try again.")
                })
        },
        async resendInvite(user_id) {
            const token = await this.$auth.getTokenSilently()
            const data = {
                user_id: this.$auth.user.value.sub,
                target_user_id: user_id
            }
            const url =
                process.env.VUE_APP_API_BASE_URL +
                `/admin/invitation/resend?lang=${this.$i18n.locale}`
            this.$axios
                .post(url, data, {
                    headers: {
                        Authorization: `Bearer ${token}` // send the access token through the 'Authorization' header
                    }
                })
                .then(response => {
                    // if (this.isLoading === true) {
                    //     this.isLoading = false
                    // }
                })
        },
        async changePassword() {
            // const token =
            //     "eyJhbGciOiJSUzI1NiIsInR5cCI6IkpXVCIsImtpZCI6InR6VEJXdWhWU25iS2VLLTZmTGdiOCJ9.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.Y3ebv0aZx7J1e6ELmMNkbZ7wEPNS3M8CQhAltjvzi24dJ32LcHyQ0m37l2va1SjmCBlPINSjAzcZlHiuorJXAvYvYRPkfvyHYhWDdYzof5pGnQ4XP_9ePq-g4ZOmsDWxX2wuPjjoZv_oiGPl_S22WGutnq6TsO-gQnBbx6q1zDaYPEwtQAfo1Pbk_DHhw1rXJAfAXhEjXm_DwJB4V7QJ3WRb5hwYx3cu0MSBkBJwErhVlNVjWXoKOcD9iu42v1723tyBAX1Ptx8xgY2Q_0zC0ByefaRkbAtIAZvtoZNZlv1PfMIxyJHmszX1uDbPJt-dwmnFlMWdIucQApkFmTFNfA"
            // const data = {
            //     password: "Aurora2021",
            //     connection: "Username-Password-Authentication"
            // }
            // console.log(this.$auth.user.value.sub)
            // this.$axios
            //     .patch(
            //         `https://flow-dev.jp.auth0.com/api/v2/users/${this.$auth.user.value.sub}`,
            //         data,
            //         {
            //             headers: {
            //                 "content-type": "application/json",
            //                 Authorization: `Bearer ${token}`
            //             }
            //         }
            //     )
            //     .then(r => console.log(r))
            this.changePasswordButton = true
            const data = {
                client_id: process.env.VUE_APP_AUTH0_CLIENT_ID,
                email: this.$auth.user.value.email,
                connection: "Username-Password-Authentication"
            }
            this.$axios
                .post(
                    `https://${process.env.VUE_APP_AUTH0_DOMAIN}/dbconnections/change_password`,
                    data,
                    {
                        headers: {
                            "content-type": "application/json"
                        }
                    }
                )
                .then(r => alert(r.data))
            setTimeout(() => {
                this.changePasswordButton = false
            }, 1200)
            // const url =
            //     "https://flow-dev.jp.auth0.com/api/v2/tickets/password-change"
            // const data = {
            //     user_id: this.$auth.user.value.sub,
            //     ttl_sec: 0,
            //     mark_email_as_verified: false,
            //     includeEmailInRedirect: false
            // }
            // const token =
            //     "eyJhbGciOiJSUzI1NiIsInR5cCI6IkpXVCIsImtpZCI6InR6VEJXdWhWU25iS2VLLTZmTGdiOCJ9.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.Bu91PRKr445DUwQ9NirZCg8RjWewUqh3mMpNY_L4WfQrrjEXY5tUkcnzoGvJYkS9MX0VM56Uc3-vN7Dia6a8Y0q_efEBLyDZx9U-dxvL5uCa1WpQ9q0oDv2Yrr0FnN6MCwCPDWoaWw6L8TM-X8LLqvWLPROXwwb638McpBJqdjPgR00ic2nNFV72yoImXpnqMo3iqVwtnDlzpgYERxyPCXmrmZtYLBfqs3c0NIXFkmxC0cn2B3-6DIriIWCehm-ED9pX_tUozmCclO9zLcrSB0JCJCuqUBnN3Ic2Rhy1pPFUz4fuqpkDPDEMlywspDE7TDOYgGVKO-Y_3AtffsSpBA"
            // this.$axios
            //     .post(url, data, {
            //         headers: {
            //             Authorization: `Bearer ${token}` // send the access token through the 'Authorization' header
            //         }
            //     })
            //     .then(res => {
            //         window.location = res.data.ticket
            //     })
        },
        compareChanges() {
            if (!this.isLoading) {
                if (this.user.role !== 1) {
                    this.unsavedChanges =
                        this.$refs.first_name.value !==
                            this.userData.profile.given_name ||
                        this.$refs.last_name.value !==
                            this.userData.profile.surname ||
                        this.$refs.job_title.value !==
                            this.userData.profile.job_title ||
                        this.$refs.email.value !==
                            this.userData.profile.email ||
                        this.$refs.locale.value !==
                            this.userData.profile.display_lang
                } else
                    this.unsavedChanges =
                        this.$refs.first_name.value !==
                            this.userData.profile.given_name ||
                        this.$refs.last_name.value !==
                            this.userData.profile.surname ||
                        this.$refs.email.value !==
                            this.userData.profile.email ||
                        this.$refs.locale.value !==
                            this.userData.profile.display_lang
            }
        },
        discardChanges() {
            if (this.user.role !== 1) {
                this.$refs.first_name.value = this.userData.profile.given_name
                this.$refs.last_name.value = this.userData.profile.surname
                this.$refs.job_title.value = this.userData.profile.job_title
                this.$refs.email.value = this.userData.profile.email
                this.$refs.locale.value = this.userData.profile.display_lang
            } else
                this.$refs.first_name.value = this.userData.profile.given_name
            this.$refs.last_name.value = this.userData.profile.surname
            this.$refs.email.value = this.userData.profile.email
            this.$refs.locale.value = this.userData.profile.display_lang
        },
        async saveSetting() {
            this.compareChanges()
            if (this.unsavedChanges) {
                const token = await this.$auth.getTokenSilently()
                let data = {}
                if (this.user.role !== 1) {
                    data = {
                        user_id: this.$auth.user.value.sub,
                        surname: this.$refs.last_name.value,
                        given_name: this.$refs.first_name.value,
                        job_title: this.$refs.job_title.value,
                        email: this.$refs.email.value,
                        display_lang: this.$refs.locale.value
                    }
                } else
                    data = {
                        user_id: this.$auth.user.value.sub,
                        surname: this.$refs.last_name.value,
                        given_name: this.$refs.first_name.value,
                        email: this.$refs.email.value,
                        display_lang: this.$refs.locale.value
                    }
                this.saveSettingButton = true
                const url =
                    process.env.VUE_APP_API_BASE_URL +
                    `/setting/admin/profile?lang=${this.$i18n.locale}`
                this.$axios
                    .put(url, data, {
                        headers: {
                            Authorization: `Bearer ${token}` // send the access token through the 'Authorization' header
                        }
                    })
                    .then(res => {
                        this.setLang(this.$refs.locale.value)
                        this.getUserData()
                        this.$emit("updateSetting")
                    })
            } else alert("Please change something first.")
            setTimeout(() => {
                this.saveSettingButton = false
            }, 1200)
        },
        async addNotification() {
            const token = await this.$auth.getTokenSilently()
            const data = {
                user_id: this.$auth.user.value.sub,
                name: this.$refs.notification_name.value,
                email: this.$refs.notification_email.value
            }
            const url =
                process.env.VUE_APP_API_BASE_URL +
                `/setting/notify/email/add?lang=${this.$i18n.locale}`
            if (data.name !== "" && data.email !== "") {
                this.$axios
                    .post(url, data, {
                        headers: {
                            Authorization: `Bearer ${token}` // send the access token through the 'Authorization' header
                        }
                    })
                    .then(res => {
                        this.getUserData()
                        this.$refs.notification_name.value = ""
                        this.$refs.notification_email.value = ""
                    })
            } else alert("Empty input.")
        },
        async removeNotification(name, email) {
            const token = await this.$auth.getTokenSilently()
            const data = {
                user_id: this.$auth.user.value.sub,
                name: name,
                email: email
            }
            const url =
                process.env.VUE_APP_API_BASE_URL +
                `/setting/notify/email/remove?lang=${this.$i18n.locale}`
            this.$axios
                .put(url, data, {
                    headers: {
                        Authorization: `Bearer ${token}` // send the access token through the 'Authorization' header
                    }
                })
                .then(res => {
                    this.getUserData()
                })
        },
        async updateNotify(type, value) {
            const token = await this.$auth.getTokenSilently()
            const data = {
                user_id: this.$auth.user.value.sub,
                type: type,
                notify: +!value
            }
            const url =
                process.env.VUE_APP_API_BASE_URL +
                `/setting/notify/option?lang=${this.$i18n.locale}`
            this.$axios
                .put(url, data, {
                    headers: {
                        Authorization: `Bearer ${token}` // send the access token through the 'Authorization' header
                    }
                })
                .then(() => {
                    this.getUserData()
                })
        },
        createAdmin() {
            this.isCreateAdmin = true
            this.$emit("disableScroll")
        },
        editAdmin(user) {
            this.$refs.admin.onInitial()
            this.isEditAdmin = true
            this.selectedAdmin = user
            this.$emit("disableScroll")
        },
        async adminSave() {
            await this.getAdminList()
            this.isCreateAdmin = false
            this.isEditAdmin = false
            this.$emit("enableScroll")
            this.$emit("updateSetting")
        },
        adminDiscard() {
            this.isCreateAdmin = false
            this.isEditAdmin = false
            this.$emit("enableScroll")
        }
    },
    mounted() {
        this.getUserData()
        this.getAdminList()
        window.onbeforeunload = () => {
            if (this.userData.profile && this.isEdit) {
                this.compareChanges()
                if (this.unsavedChanges) {
                    return true
                } else return null
            }
        }
    },
    beforeRouteLeave(to, from, next) {
        if (this.userData.profile && this.isEdit) {
            setTimeout(() => {
                this.compareChanges()
                if (this.unsavedChanges) {
                    const answer = window.confirm(
                        this.$i18n.locale === "en"
                            ? "Changes are not saved yet. Do you want to leave this page?"
                            : "變更尚未儲存，你要離開此頁嗎？"
                    )
                    if (answer) {
                        next()
                    } else {
                        next(false)
                    }
                } else next()
            }, 500)
        } else next()
    },
    data() {
        return {
            userData: {},
            isLoading: true,
            isEdit: false,
            changePasswordButton: false,
            saveSettingButton: false,
            isCreateAdmin: false,
            isEditAdmin: false,
            unsavedChanges: false,
            currentPage: 1,
            adminList: [],
            selectedAdmin: {}
        }
    }
}
</script>

<style scoped></style>
